import { pickT } from "@repo/shared";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { T } from "./settings-xray-form.i18n";

type SettingsXrayFormProps = {
  flowLanguage: string;
  hasXRayAccess: boolean;
  displayMode: string;
  onDisplayModeChange: (mode: string) => void;
  showChart: boolean;
  onShowChartChange: (show: boolean) => void;
  showTable: boolean;
  onShowTableChange: (show: boolean) => void;
  enableFollowupComparison: boolean;
  onEnableFollowupComparisonChange: (enable: boolean) => void;
};

export function SettingsXrayForm({
  flowLanguage,
  hasXRayAccess,
  displayMode,
  onDisplayModeChange,
  showChart,
  onShowChartChange,
  showTable,
  onShowTableChange,
  enableFollowupComparison,
  onEnableFollowupComparisonChange,
}: SettingsXrayFormProps) {
  const t = pickT(T, flowLanguage);

  return (
    <div className="space-y-4">
      {!hasXRayAccess && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.xrayNoAccess}
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="xray-display-mode">
          {t.displayMode} <span className="text-destructive">{t.required}</span>
        </Label>
        <Select value={displayMode} onValueChange={onDisplayModeChange}>
          <SelectTrigger id="xray-display-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="asset_allocation">{t.assetAllocation}</SelectItem>
            <SelectItem value="country_exposure">{t.countryExposure}</SelectItem>
            <SelectItem value="regional_exposure">{t.regionalExposure}</SelectItem>
            <SelectItem value="stock_sector">{t.stockSector}</SelectItem>
            <SelectItem value="equity_style">{t.equityStyleBox}</SelectItem>
            <SelectItem value="bond_sector">{t.bondSector}</SelectItem>
            <SelectItem value="top_holdings">{t.topHoldings}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t.xrayDisplayModeHelp}</p>
      </div>

      {displayMode !== "top_holdings" && displayMode !== "equity_style" && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="xray-show-chart" className="text-base">
                {t.showChart}
              </Label>
              <p className="text-sm text-muted-foreground">{t.showChartDesc}</p>
            </div>
            <Switch id="xray-show-chart" checked={showChart} onCheckedChange={onShowChartChange} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="xray-show-table" className="text-base">
                {t.showTable}
              </Label>
              <p className="text-sm text-muted-foreground">{t.showTableDesc}</p>
            </div>
            <Switch id="xray-show-table" checked={showTable} onCheckedChange={onShowTableChange} />
          </div>

          {!showChart && !showTable && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {t.atLeastOneOption}
            </div>
          )}
        </>
      )}

      {/* Comparison is supported for the pie modes and equity_style, not top_holdings */}
      {displayMode !== "top_holdings" && (
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="xray-followup-comparison" className="text-base">
              {t.enableFollowupComparison}
            </Label>
            <p className="text-sm text-muted-foreground">{t.enableFollowupComparisonDesc}</p>
          </div>
          <Switch
            id="xray-followup-comparison"
            checked={enableFollowupComparison}
            onCheckedChange={onEnableFollowupComparisonChange}
          />
        </div>
      )}
    </div>
  );
}
